/**
 * @fileoverview Authentication Configuration
 * Configures NextAuth.js authentication with MFour credentials provider
 * @module @auth/config/auth
 */

// === Next.js ===
import { getServerSession as getServerSessionInternal } from 'next-auth';
import CredentialsProvider from 'next-auth/providers/credentials';
import { redirect } from 'next/navigation';

// === Types & Interfaces ===
import type { TeamMember } from '@/@account/types';
import type { AuthOptions, Session, User } from 'next-auth';
import type { JWT } from 'next-auth/jwt';
import type { OrganizationProfile, UserProfile } from '../types';

// === Routes ===
import { Routes } from '@/app/routes';

// === Services ===
import AuthService from '../services';

// === Config ===
import { tracking } from '@/lib/features/tracking';
import { authLogger } from './auth-logger';
import { AUTH_CONFIG, sessionCookieName } from './config';

// === Server-side Profile Cache ===
// Simple in-memory cache to avoid excessive API calls during session checks
// Profile data is NOT stored in the JWT cookie to prevent 431 errors
interface CachedProfile {
    profile: UserProfile;
    userProfile: TeamMember;
    orgProfile: OrganizationProfile;
    hasStudioAIAccess: boolean;
    hasClientStudioAIAccess: boolean;
    freemiumUsage?: {
        hasReachedLimit: boolean;
        currentCount: number;
        limit: number;
        limitType: 'message' | 'token';
        daysUntilReset: number | null;
        limitMessage: string | null;
    };
    cachedAt: number;
}

const profileCache = new Map<string, CachedProfile>();
const PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Clears the server-side profile cache for a specific user, or all users if no userId is provided.
 * Should be called on sign-out to prevent stale data on re-login.
 */
export function clearProfileCache(userId: string): void {
    profileCache.delete(userId);
}

/**
 * Gets cached profile data or fetches fresh data if cache is stale/missing
 * This runs server-side only - profile data is NOT stored in JWT cookies
 */
async function getCachedProfileData(
    idToken: string,
    userId: string,
): Promise<CachedProfile | null> {
    const cacheKey = userId;
    const cached = profileCache.get(cacheKey);
    const now = Date.now();

    // Return cached data if still valid
    if (cached && now - cached.cachedAt < PROFILE_CACHE_TTL) {
        return cached;
    }

    // Fetch fresh profile data
    try {
        const [profileResult, userProfileResult, orgProfileResult, studioAIAccessResult] =
            await Promise.all([
                AuthService.getProfile(idToken).catch((error) => {
                    tracking.errorWithSource(error as Error, 'profileCache.getProfile', {
                        message: 'Failed to fetch profile',
                    });
                    return null;
                }),
                AuthService.getUserProfile(idToken).catch((error) => {
                    tracking.errorWithSource(error as Error, 'profileCache.getUserProfile', {
                        message: 'Failed to fetch user profile',
                    });
                    return null;
                }),
                AuthService.getOrgProfile(idToken).catch((error) => {
                    tracking.errorWithSource(error as Error, 'profileCache.getOrgProfile', {
                        message: 'Failed to fetch org profile',
                    });
                    return null;
                }),
                AuthService.getStudioAIAccess(idToken).catch((error) => {
                    tracking.errorWithSource(error as Error, 'profileCache.getStudioAIAccess', {
                        message: 'Failed to fetch Studio AI access status',
                    });
                    return null;
                }),
            ]);

        if (profileResult && userProfileResult && orgProfileResult) {
            const clientStudioAIAccess = studioAIAccessResult?.clientStudioAIAccess;
            const cacheEntry: CachedProfile = {
                profile: profileResult,
                userProfile: userProfileResult,
                orgProfile: orgProfileResult,
                hasStudioAIAccess: studioAIAccessResult?.hasAccess ?? false,
                hasClientStudioAIAccess: clientStudioAIAccess?.hasAccess ?? false,
                freemiumUsage: studioAIAccessResult?.freemiumUsage,
                cachedAt: now,
            };
            profileCache.set(cacheKey, cacheEntry);

            // Clean up old cache entries periodically (keep max 1000 entries)
            if (profileCache.size > 1000) {
                const oldestKey = profileCache.keys().next().value;
                if (oldestKey) profileCache.delete(oldestKey);
            }

            return cacheEntry;
        }

        // If fetch failed but we have stale cached data, return it
        if (cached) {
            return cached;
        }

        return null;
    } catch (error) {
        tracking.errorWithSource(error as Error, 'profileCache.fetch', {
            message: 'Failed to fetch profile data',
        });
        // Return stale cache if available
        return cached || null;
    }
}

/**
 * NextAuth configuration options
 * Configures authentication providers, callbacks, and session handling
 */
export const authOptions: AuthOptions = {
    providers: [
        CredentialsProvider({
            id: 'mfour-credentials',
            name: 'MFour Credentials',
            credentials: {
                email: { label: 'Email', type: 'text' },
                password: { label: 'Password', type: 'password' },
            },
            async authorize(credentials): Promise<User | null> {
                if (!credentials?.email || !credentials?.password) {
                    tracking.errorWithSource(
                        new Error('No credentials provided'),
                        'authOptions.authorize',
                    );
                    return null;
                }

                try {
                    // === Production Authentication ===
                    const authResult = await AuthService.signIn({
                        email: credentials.email,
                        password: credentials.password,
                    });

                    if (!authResult.user) {
                        tracking.errorWithSource(
                            new Error('No user found'),
                            'authOptions.authorize',
                        );
                        return null;
                    }
                    return {
                        id: authResult.user.id,
                        isUnaffiliated: authResult.user.isUnaffiliated ?? false,
                        email: authResult.user.email,
                        name: authResult.user.name,
                        accessToken: authResult.AccessToken,
                        refreshToken: authResult.RefreshToken,
                        idToken: authResult.IdToken,
                        groups: authResult.user.groups,
                        role: authResult.user.role,
                        isAdmin: authResult.user.isAdmin,
                        isInternal: authResult.user.isInternal,
                        givenName: authResult.user.givenName,
                        familyName: authResult.user.familyName,
                        salesforceOrgId: authResult.user.salesforceOrgId,
                        salesforceOrg: authResult.user.salesforceOrg,
                    };
                } catch (error: any) {
                    tracking.errorWithSource(error as Error, 'authOptions.authorize');
                    // If the error has a message, throw it so NextAuth can pass it to the frontend
                    if (error?.message) {
                        throw new Error(error.message);
                    }
                    throw error;
                }
            },
        }),
    ],
    callbacks: {
        // === JWT Token Management ===
        // IMPORTANT: Only store tokens and minimal data here to avoid 431 errors
        // Profile data is fetched server-side in the session callback, NOT stored in JWT
        async jwt({ token, user }): Promise<JWT> {
            // Initial sign in - store ONLY essential token data
            // DO NOT store profile objects here - they bloat the cookie
            if (user) {
                token.sub = user.id;
                token.email = user.email;
                token.accessToken = user.accessToken;
                token.refreshToken = user.refreshToken;
                token.idToken = user.idToken;
                token.initialIat = Math.floor(Date.now() / 1000);
            }

            // Check for token refresh (every 30 minutes)
            const initialIat = token.initialIat || token.iat || 0;
            const timeSinceIssued = Math.floor(Date.now() / 1000) - (initialIat as number);

            if (timeSinceIssued > 32400 && token.refreshToken) {
                try {
                    const authResult = await AuthService.refreshToken(token.refreshToken as string);
                    token.accessToken = authResult.AccessToken;
                    token.refreshToken = authResult.RefreshToken || token.refreshToken;
                    token.idToken = authResult.IdToken;
                    token.initialIat = Math.floor(Date.now() / 1000);
                    // Clear server-side profile cache when tokens are refreshed
                    if (token.sub) {
                        profileCache.delete(token.sub as string);
                    }
                } catch (error) {
                    tracking.errorWithSource(error as Error, 'authOptions.jwt.refresh', {
                        hasRefreshToken: Boolean(token.refreshToken),
                        message: 'Failed to refresh token',
                    });
                    token.error = 'RefreshAccessTokenError';
                }
            }

            return token;
        },

        // === Session Management ===
        // Profile data is fetched here (server-side) and returned in the response body
        // This keeps the JWT cookie small while still providing full session data
        async session({ session, token }: { session: Session; token: JWT }): Promise<Session> {
            if (!token || !session.user) {
                throw new Error('Unauthorized');
            }

            // Check for token errors requiring re-authentication
            if (token.error === 'RefreshAccessTokenError' || token.error === 'TokenExpiredError') {
                throw new Error('Session expired');
            }

            // Fetch profile data from server-side cache (NOT stored in JWT)
            const idToken = token.idToken as string;
            const userId = token.sub as string;

            if (!idToken || !userId) {
                throw new Error('Invalid session');
            }

            const cachedProfile = await getCachedProfileData(idToken, userId);

            if (!cachedProfile) {
                tracking.errorWithSource(
                    new Error('Failed to fetch profile data'),
                    'authOptions.session',
                    { message: 'Could not fetch or cache profile data' },
                );
                throw new Error('Invalid session');
            }

            const {
                profile,
                userProfile,
                orgProfile,
                hasStudioAIAccess,
                hasClientStudioAIAccess,
                freemiumUsage,
            } = cachedProfile;

            // Validate critical profile properties
            if (!profile.id || !profile.email) {
                tracking.errorWithSource(
                    new Error('Corrupted profile data'),
                    'authOptions.session',
                    { message: 'Profile missing required fields (id, email)' },
                );
                throw new Error('Invalid session');
            }

            // Shape session with profile data (returned in response body, not cookie)
            session.user.id = profile.id;
            // profile.id is the Cognito username, so the Studio database key
            // comes from /studio/v1/users/me, which returns org_user.org_user_id.
            session.user.orgUserId = userProfile.userId;
            session.user.email = profile.email;
            session.user.name = profile.name;
            session.user.groups = profile.groups ?? [];
            session.user.role = profile.role ?? '';
            session.user.isAdmin = profile.isAdmin ?? false;
            session.user.isInternal = profile.isInternal ?? false;
            session.user.givenName = profile.givenName;
            session.user.familyName = profile.familyName;
            session.user.hasStudioAIAccess = hasStudioAIAccess;
            session.user.isUnaffiliated = profile.isUnaffiliated ?? false;
            session.user.stripeCustomerId = userProfile.stripeCustomerId;
            session.user.profileImage = userProfile.profileImage;
            session.user.freemiumUsage = freemiumUsage;
            session.user.hasSubsidiaryAccess = profile.hasSubsidiaryAccess ?? false;
            session.user.crossSubsidiaryWriteEnabled = profile.crossSubsidiaryWriteEnabled ?? false;

            session.org = {
                ...orgProfile,
                hasClientStudioAIAccess,
            };

            session.subsidiary = {
                role: profile.subsidiaryRole ?? 'standalone',
                parentOrgId: profile.parentOrgId ?? null,
                orgIds: profile.subsidiaryOrgIds ?? [],
                sfAccountIds: profile.subsidiarySfAccountIds ?? [],
                options: [],
            };

            // Set session tokens
            session.accessToken = token.accessToken as string;
            session.refreshToken = token.refreshToken as string;
            session.idToken = token.idToken as string;

            return session;
        },

        // === Sign In Flow ===
        async signIn(): Promise<boolean> {
            return true;
        },

        // === Redirect Handling ===
        async redirect({ url, baseUrl }): Promise<string> {
            // `baseUrl` has been observed falsy here in practice — `${baseUrl}${url}`
            // then silently produces a literal "undefined" prefix via JS string
            // coercion (e.g. "undefined/sign-in?..."), which was a confirmed,
            // reproducing contributor to an infinite redirect loop. Never
            // concatenate a falsy baseUrl; fall back to the configured origin.
            const safeBaseUrl = baseUrl || process.env.NEXTAUTH_URL || '';
            // Handle relative URLs
            if (url.startsWith('/')) {
                return `${safeBaseUrl}${url}`;
            }
            // Handle absolute URLs
            if (url.startsWith('http')) {
                return url;
            }
            return safeBaseUrl;
        },
    },

    // === Event Handlers ===
    events: {
        async signOut({ token }) {
            if (token?.sub) {
                clearProfileCache(token.sub as string);
            }
        },
    },

    // === Page Configuration ===
    pages: {
        signIn: Routes.SIGNIN,
        error: '/api/auth/error',
        signOut: Routes.SIGNOUT,
    },

    // === Session Configuration ===
    session: {
        strategy: 'jwt',
        maxAge: AUTH_CONFIG.tokens.accessTokenExpiry,
    },

    // === Cookie Configuration ===
    cookies: {
        sessionToken: {
            name: sessionCookieName(),
            options: AUTH_CONFIG.cookies.options,
        },
    },

    debug: process.env.NODE_ENV === 'development',
    logger: authLogger,
};

// === Helper Functions ===
/**
 * Gets the current server session without passing authOptions
 * @returns Promise with the current session or null
 */
export async function getServerSession(): Promise<Session | null> {
    try {
        // No session is the normal signed-out state, not an error. The middleware
        // calls this on every request, so reporting it would put two error lines
        // in the server log for every signed-out visit.
        return await getServerSessionInternal(authOptions);
    } catch (error) {
        tracking.errorWithSource(error as Error, 'getServerSession');
        return null;
    }
}

/**
 * Gets the required server session with type safety and redirection
 * @param redirectTo - Optional URL to redirect to after authentication
 * @returns Promise with the current session
 * @throws {UnauthorizedError} If no valid session is found
 */
export async function getRequiredServerSession(redirectTo?: string): Promise<Session> {
    const session = await getServerSession();

    if (!session) {
        const signInUrl = new URL(Routes.SIGNIN, process.env.NEXTAUTH_URL);
        if (redirectTo) {
            signInUrl.searchParams.set('callbackUrl', redirectTo);
        }

        tracking.navigation(signInUrl.toString(), Routes.SIGNIN);
        // Use Next.js redirect() for proper server-side redirection
        redirect(signInUrl.toString());
    }

    return session as Session; // Safe to cast since we redirect if null
}

// === TODO List ===
/**
 * @todo Add proper error handling with custom error types - Create a dedicated error type system for better error handling and type safety
 * @todo Add proper session validation with Zod - Implement Zod schemas for runtime type safety of session data
 * @todo Add proper session revocation - Implement mechanism for explicitly revoking active sessions
 */
